import { DASHBOARD_DATA_TYPE_ENUM } from '@/enums/dash-progress-data-type.enum'
import MathManager from '@/utils/math-manager.utils'
import TextManager from '@/utils/text-manager.utils'

class DashProgressManager {
  list = []
  colorList = ['#5691D6', '#8267D3', '#F3B700']
  currencySimbol = 'R$'
  TPVkey = 'tpv'

  constructor (list, total = 0) {
    if (!list) throw new Error('Missing argument list!')

    this.list = [...list]
    this.total = total
  }

  /**
   *
   * @param {*} dataDefault
   * @returns
   */
  createStructure (dataDefault) {
    return dataDefault.reduce((acc, curr) => {
      return {
        ...acc,
        [curr]: {
          name: curr,
          percentage: 0,
          quantity: 0,
          key: curr,
          color: '',
          leftIcon: {
            name: '',
            tooltip: null
          },
          rightIcon: {
            name: '',
            tooltip: null
          }
        }
      }
    }, {})
  }

  /**
   * order by sort type and propertie key
   * @param {String} sortType sorting type [asc | desc]
   * @param {String} key property of the data to be sorted
   * @returns Array
   */
  sortByKey (sortType = 'desc', key = 'quantity') {
    const list = this.getList()
    if (!Object.prototype.hasOwnProperty.call(list?.[0], key)) {
      throw new Error('Missing property')
    }

    const TYPE_ORDER = ['asc', 'desc']

    if (!TYPE_ORDER.includes(sortType)) {
      throw new Error('Expected value asc or desc')
    }

    const validation = {
      asc: () => list.sort((a, b) => a[key] - b[key]),
      desc: () => list.sort((a, b) => b[key] - a[key])
    }

    this.setList(validation[sortType]())
  }

  getCurrency (value = 0) {
    return value
      ? `${this.currencySimbol} ${MathManager.formatCurrencyBRL(value)}`
      : `${this.currencySimbol} 0,00`
  }

  getColor (elementIndex = 0, colorList = this.colorList) {
    return colorList[elementIndex] ?? colorList[0]
  }

  /**
   * Used to sum a list of objects based on a key
   * @param {Array} items list sumed
   * @param {Array} keys list of properties keys to be summed
   * @returns new object with the sum of the last items
   */
  sumByKeys (items = [], keys = ['quantity']) {
    return MathManager.sumByKeys(items, keys)
  }

  sumRemainingItems () {
    const [first, second, ...others] = this.getList()

    const third = {
      others,
      ...this.sumByKeys(others, ['percentage', this.TPVkey, 'quantity'])
    }

    this.setList([first, second, third])
  }

  /**
   * Modifies the `others` list to include the percentage and value
   * @param Array others
   * @returns
   */
  adapterTooltipRemaining (others, total = this.total) {
    return others.map(element => ({
      name: element.name,
      quantity: element.quantity,
      [DASHBOARD_DATA_TYPE_ENUM.PERCENTAGE]: MathManager.percentageCalculator(
        element.quantity,
        total
      ),
      [DASHBOARD_DATA_TYPE_ENUM.VALUE]: this.getCurrency(element[this.TPVkey])
    }))
  }

  adapterRemainingItems (parseLabelFn) {
    const OTHERS_NAME = 'others'

    const [first, second, remaining] = this.getList()

    remaining.name = parseLabelFn(OTHERS_NAME)
    remaining.key = OTHERS_NAME
    remaining.leftIcon = {
      name: OTHERS_NAME,
      tooltip: null
    }
    remaining.rightIcon = {
      name: 'tooltip',
      tooltip: this.adapterTooltipRemaining(remaining.others)
    }

    remaining.value = this.getCurrency(remaining[this.TPVkey])

    remaining.color = this.getColor(2)

    this.setList([first, second, remaining])
  }

  /**
   * adapter modifies the elements the list in the specific way we want,
   * adding extra properties to the array of objects like leftIcon, key, name, etc...
   *
   * The function creates a constant STRUCTURE_DEFAULT that defines a default structure for each
   * element of the list object.
   * @param {Array} structureDefault data
   * @param {Function} parseLabelFn
   * @returns Array
   */
  adapter (structureDefault, parseLabelFn, colorList = this.colorList) {
    const listAdapter = this.getList().map((element, index) => {
      const item = { ...structureDefault[element.name], ...element }

      const name = item.name?.toLowerCase()
      item.key = name
      item.name =
        parseLabelFn(item.name) ?? TextManager.formatTextSnakeOrCamel(item.name)
      item.color = this.getColor(index, colorList)
      item.leftIcon = { name, tooltip: null }

      item.percentage = MathManager.percentageCalculator(
        item.quantity,
        this.total
      )

      item.value = this.getCurrency(item[this.TPVkey])

      return item
    })

    this.setList(listAdapter)
  }

  /**
   * Executes all the methods
   * @returns Array
   */
  get (structure, parseLabel) {
    if (this.getList().length <= 0) return []

    this.sortByKey()

    const defaultStructure = this.createStructure(structure)
    this.adapter(defaultStructure, parseLabel)

    if (this.getList().length < 3) {
      return this.getList()
    }

    this.sumRemainingItems()

    this.adapterRemainingItems(parseLabel)

    return this.getList()
  }

  getList () {
    return this.list
  }

  setList (list) {
    this.list = list
  }
}

export default DashProgressManager
